import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Dummy data for meetings with expanded details
const dummyMeetings = [
  {
    id: 1,
    title: 'Weekly Team Sync - Q4 Planning',
    date: '2025-10-15',
    attendees: 8,
    status: 'Completed',
    description: 'Team alignment on Q4 goals and milestones.',
    agenda: ['Review Q3 achievements', 'Set Q4 targets', 'Assign action items'],
    minutes: 'Discussed quarterly targets; John to lead marketing push. Next meeting: Oct 22.',
    attendeesList: ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Brown', 'Charlie Davis', 'Diana Evans', 'Eve Wilson', 'Frank Miller']
  },
  {
    id: 2,
    title: 'Product Roadmap Review',
    date: '2025-10-10',
    attendees: 12,
    status: 'In Progress',
    description: 'Review and refine the upcoming product features.',
    agenda: ['Current roadmap overview', 'Feedback session', 'Prioritization vote'],
    minutes: 'Roadmap approved with adjustments; prioritize AI integration. Pending: Final vote on feature X.',
    attendeesList: ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Brown', 'Charlie Davis', 'Diana Evans', 'Eve Wilson', 'Frank Miller', 'Grace Lee', 'Henry Taylor', 'Ivy Clark', 'Jack White']
  },
  {
    id: 3,
    title: 'Budget Allocation Meeting',
    date: '2025-10-05',
    attendees: 6,
    status: 'Completed',
    description: 'Allocate departmental budgets for the fiscal year.',
    agenda: ['Current budget review', 'Department requests', 'Approval and adjustments'],
    minutes: 'Budgets approved; Marketing gets 15% increase. Action: Update financial reports.',
    attendeesList: ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Brown', 'Charlie Davis', 'Diana Evans']
  },
  {
    id: 4,
    title: 'Client Feedback Session',
    date: '2025-10-01',
    attendees: 4,
    status: 'Completed',
    description: 'Gather and analyze feedback from key clients.',
    agenda: ['Client surveys review', 'Pain points discussion', 'Improvement plan'],
    minutes: 'Identified UI issues; plan to roll out fixes next sprint. Client satisfaction score: 8.2/10.',
    attendeesList: ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Brown']
  },
  {
    id: 5,
    title: 'Engineering Standup',
    date: '2025-09-28',
    attendees: 10,
    status: 'Completed',
    description: 'Daily standup for engineering team updates.',
    agenda: ['Blockers and progress', 'Today\'s tasks', 'Quick Q&A'],
    minutes: 'No major blockers; deployment successful. Focus on bug fixes today.',
    attendeesList: ['Bob Johnson', 'Charlie Davis', 'Diana Evans', 'Eve Wilson', 'Frank Miller', 'Grace Lee', 'Henry Taylor', 'Ivy Clark', 'Jack White', 'Karl Green']
  }
];

// Dummy data for tabs
const dummyMembers = [
  { id: 1, name: 'John Doe', role: 'Manager', email: 'john@company.com' },
  { id: 2, name: 'Jane Smith', role: 'Developer', email: 'jane@company.com' },
  { id: 3, name: 'Bob Johnson', role: 'Designer', email: 'bob@company.com' },
  { id: 4, name: 'Alice Brown', role: 'QA Engineer', email: 'alice@company.com' },
  { id: 5, name: 'Charlie Davis', role: 'Product Owner', email: 'charlie@company.com' }
];

const dummyLocations = [
  { id: 1, name: 'HQ Office', address: '123 Main St, City, State 12345' },
  { id: 2, name: 'Branch Office', address: '456 Oak Ave, Town, State 67890' },
  { id: 3, name: 'Remote - Virtual', address: 'Online via Zoom' },
  { id: 4, name: 'Conference Room A', address: 'Within HQ Office' }
];

const dummyDistributions = [
  { id: 1, name: 'Team Distro', emails: ['john@company.com', 'jane@company.com', 'bob@company.com'] },
  { id: 2, name: 'All Staff', emails: ['all@company.com'] },
  { id: 3, name: 'Exec Team', emails: ['ceo@company.com', 'cfo@company.com', 'cto@company.com'] },
  { id: 4, name: 'Project Leads', emails: ['lead1@company.com', 'lead2@company.com'] }
];

const dummyExpertTips = [
  { id: 1, tip: 'Always start with clear objectives to keep discussions focused.' },
  { id: 2, tip: 'Summarize key decisions and action items at the end.' },
  { id: 3, tip: 'Use visuals like charts for better engagement.' },
  { id: 4, tip: 'Follow up on unresolved items in the next meeting.' },
  { id: 5, tip: 'Record attendance and contributions for accountability.' }
];

const dummyPurposes = [
  { id: 1, userType: 'Corporate', canUse: 'Yes', why: 'Large companies rely on formal MOM for board meetings, documenting votes, reports, and strategies to ensure governance and legal compliance.' },
  { id: 2, userType: 'MSMEs', canUse: 'Yes', why: 'Micro, small, and medium enterprises use informal or action-focused MOM for operational meetings, tracking tasks and decisions without heavy bureaucracy.' },
  { id: 3, userType: 'Business Person', canUse: 'Yes', why: 'Individual entrepreneurs or owners use MOM for client negotiations, partnerships, or solo planning sessions to maintain records for audits or future reference.' },
  { id: 4, userType: 'Professional', canUse: 'Yes', why: 'Freelancers, consultants, or specialists (e.g., in medical or legal fields) use MOM for client consultations or team collaborations to summarize outcomes and action items.' },
  { id: 5, userType: 'Groups', canUse: 'Yes', why: 'Community, project, or volunteer groups employ MOM to capture discussions and assignments, ensuring alignment and continuity among members.' },
  { id: 6, userType: 'Society', canUse: 'Yes', why: 'Housing societies, NGOs, or civic organizations use MOM for committee meetings to record resolutions, budgets, and community decisions.' },
  { id: 7, userType: 'Government', canUse: 'Yes', why: 'Public sector bodies mandate formal MOM for policy discussions, hearings, and approvals, often as legal evidence in proceedings.' }
];

const MOMHome = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(null); // null for meetings view
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const navigate = useNavigate();

  // Filter meetings based on search term (searches in title)
  const filteredMeetings = dummyMeetings.filter(meeting =>
    meeting.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleMeetingClick = (meeting) => {
    setSelectedMeeting(meeting);
    setActiveTab(null); // Ensure we're in meetings view
  };

  const handleCloseDetails = () => {
    setSelectedMeeting(null);
  };

  const handleNewMeeting = () => {
    navigate('/mom-new');
  };

  const handleTabClick = (tabName) => {
    if (tabName === 'New Meeting') {
      handleNewMeeting();
    } else {
      setActiveTab(tabName);
      setSelectedMeeting(null); // Close any open details
    }
  };

  const handleBackToMeetings = () => {
    setActiveTab(null);
  };

  // Render list items generically for reusability
  const renderList = (items, renderItem) => (
    <ul style={styles.list}>
      {items.map((item) => (
        <li key={item.id} style={styles.listItem}>
          {renderItem(item)}
        </li>
      ))}
    </ul>
  );

  // Dark green-themed styles (mobile-first: flex/grid responsive)
  const styles = {
    container: {
      backgroundColor: '#001a00', // Dark green background
      color: '#ffffff',
      minHeight: '100vh',
      padding: '1rem',
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box'
    },
    header: {
      textAlign: 'center',
      marginBottom: '1.5rem',
      fontSize: '1.5rem',
      color: '#4caf50' // Green accent
    },
    searchBox: {
      width: '100%',
      padding: '0.75rem',
      marginBottom: '1.5rem',
      backgroundColor: '#002200', // Darker green-gray
      color: '#ffffff',
      border: '1px solid #4caf50',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      outline: 'none',
      boxSizing: 'border-box'
    },
    tabsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '0.75rem',
      marginBottom: '1.5rem'
    },
    tabButton: {
      padding: '0.75rem',
      backgroundColor: '#002200',
      color: '#ffffff',
      border: '1px solid #4caf50',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.875rem',
      transition: 'background-color 0.2s ease'
    },
    tabButtonActive: {
      backgroundColor: '#4caf50',
      color: '#001a00'
    },
    backButton: {
      padding: '0.75rem 1rem',
      backgroundColor: '#002200',
      color: '#4caf50',
      border: '1px solid #4caf50',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontSize: '1rem',
      marginBottom: '1rem',
      display: 'block',
      width: 'fit-content'
    },
    meetingsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    list: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    listItem: {
      padding: '1rem',
      marginBottom: '0.5rem',
      backgroundColor: '#002200',
      borderRadius: '0.5rem',
      border: '1px solid #4caf50',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    meetingItem: {
      cursor: 'pointer'
    },
    meetingItemHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)'
    },
    meetingTitle: {
      margin: '0 0 0.5rem 0',
      fontSize: '1.125rem',
      color: '#4caf50'
    },
    meetingDetails: {
      margin: '0 0 0.5rem 0',
      fontSize: '0.875rem',
      opacity: 0.8
    },
    detailsSection: {
      backgroundColor: '#002200',
      border: '1px solid #4caf50',
      borderRadius: '0.5rem',
      padding: '1rem',
      marginTop: '1rem'
    },
    detailsTitle: {
      fontSize: '1.25rem',
      color: '#4caf50',
      marginBottom: '0.5rem'
    },
    detailsSubtitle: {
      fontSize: '1rem',
      color: '#ffffff',
      margin: '0.5rem 0',
      fontWeight: 'bold'
    },
    detailsText: {
      fontSize: '0.875rem',
      opacity: 0.9,
      margin: '0.25rem 0'
    },
    agendaList: {
      listStyleType: 'disc',
      paddingLeft: '1.5rem',
      margin: '0.5rem 0'
    },
    agendaItem: {
      fontSize: '0.875rem',
      margin: '0.25rem 0'
    },
    attendeesList: {
      fontSize: '0.75rem',
      opacity: 0.8
    },
    noItems: {
      textAlign: 'center',
      color: '#cccccc',
      fontStyle: 'italic',
      padding: '2rem'
    },
    closeButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#4caf50',
      color: '#001a00',
      border: 'none',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      fontSize: '0.875rem',
      marginTop: '1rem'
    }
  };

  // Render meeting details
  const renderMeetingDetails = (meeting) => (
    <div style={styles.detailsSection}>
      <h2 style={styles.detailsTitle}>{meeting.title}</h2>
      <p style={styles.detailsSubtitle}>Date: {meeting.date}</p>
      <p style={styles.detailsSubtitle}>Status: {meeting.status}</p>
      <p style={styles.detailsSubtitle}>Attendees: {meeting.attendees}</p>
      <p style={styles.detailsText}><strong>Description:</strong> {meeting.description}</p>
      <div>
        <p style={styles.detailsSubtitle}>Agenda:</p>
        <ul style={styles.agendaList}>
          {meeting.agenda.map((item, index) => (
            <li key={index} style={styles.agendaItem}>{item}</li>
          ))}
        </ul>
      </div>
      <p style={styles.detailsText}><strong>Minutes:</strong> {meeting.minutes}</p>
      <div>
        <p style={styles.detailsSubtitle}>Attendees:</p>
        <ul style={styles.attendeesList}>
          {meeting.attendeesList.map((attendee, index) => (
            <li key={index}>{attendee}</li>
          ))}
        </ul>
      </div>
      <button onClick={handleCloseDetails} style={styles.closeButton}>
        Close Details
      </button>
    </div>
  );

  // Render generic list for tabs
  const renderTabContent = (tabName) => {
    switch (tabName) {
      case 'Members':
        return renderList(dummyMembers, (member) => (
          <>
            <h3 style={styles.meetingTitle}>{member.name}</h3>
            <p style={styles.meetingDetails}>Role: {member.role} | Email: {member.email}</p>
          </>
        ));
      case 'Locations':
        return renderList(dummyLocations, (location) => (
          <>
            <h3 style={styles.meetingTitle}>{location.name}</h3>
            <p style={styles.meetingDetails}>Address: {location.address}</p>
          </>
        ));
      case 'Distribution':
        return renderList(dummyDistributions, (distro) => (
          <>
            <h3 style={styles.meetingTitle}>{distro.name}</h3>
            <p style={styles.meetingDetails}>Emails: {distro.emails.join(', ')}</p>
          </>
        ));
      case 'Seek MOM Expert':
        return renderList(dummyExpertTips, (tip) => (
          <p style={{ ...styles.meetingDetails, marginBottom: '0.5rem' }}>{tip.tip}</p>
        ));
      case "Purpose":
        return (
          <div className="w-full overflow-x-auto">
            {/* Desktop Table for md+ screens */}
            <table className="hidden md:table w-full border border-green-500 rounded-lg">
              <thead className="bg-green-700 text-green-100">
                <tr>
                  <th className="px-4 py-2 border border-green-500">🧑‍💼 User Type</th>
                  <th className="px-4 py-2 border border-green-500">✅ Can Use MOM?</th>
                  <th className="px-4 py-2 border border-green-500">💡 Why/How It's Used</th>
                </tr>
              </thead>
              <tbody>
                {dummyPurposes.map((purpose) => (
                  <tr
                    key={purpose.id}
                    className="border border-green-500 hover:bg-green-900 transition-colors"
                  >
                    <td className="px-4 py-2 border border-green-500">{purpose.userType}</td>
                    <td className="px-4 py-2 border border-green-500">{purpose.canUse}</td>
                    <td className="px-4 py-2 border border-green-500">{purpose.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards for small screens */}
            <div className="flex flex-col space-y-4 md:hidden mt-2">
              {dummyPurposes.map((purpose) => (
                <div
                  key={purpose.id}
                  className="bg-green-900/60 border border-green-500 rounded-lg p-4 hover:bg-green-800 transition"
                >
                  <p className="text-green-300 font-semibold">🧑‍💼 {purpose.userType}</p>
                  <p className="text-green-100">✅ Can Use MOM? {purpose.canUse}</p>
                  <p className="text-green-100 text-sm mt-2">💡 {purpose.why}</p>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const currentView = activeTab || (selectedMeeting ? 'details' : 'meetings');

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Minutes of Meetings</h1>
      
      {/* Search Box - only show in meetings view */}
      {currentView === 'meetings' && (
        <input
          type="text"
          placeholder="Search meetings by title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchBox}
        />
      )}

      {/* Tabs as Grid of Buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-6 place-items-center">
        <button
          onClick={() => handleTabClick('New Meeting')}
          className={`w-full px-4 py-3 rounded-lg border border-green-500 text-sm font-medium transition-colors duration-200
            ${activeTab === 'New Meeting' ? 'bg-green-400 text-yellow-200 shadow-md' : 'bg-green-600  text-white hover:bg-green-700'}`}
        >
          🆕 New Meeting
        </button>

        <button
          onClick={() => handleTabClick('Members')}
          className={`w-full px-4 py-3 rounded-lg border border-green-500 text-sm font-medium transition-colors duration-200
            ${activeTab === 'Members' ? 'bg-green-400 text-yellow-200 shadow-md' : 'bg-green-600  text-white hover:bg-green-700'}`}
        >
          👥 Members
        </button>

        <button
          onClick={() => handleTabClick('Locations')}
          className={`w-full px-4 py-3 rounded-lg border border-green-500 text-sm font-medium transition-colors duration-200
            ${activeTab === 'Locations' ? 'bg-green-400 text-yellow-200 shadow-md' : 'bg-green-600  text-white hover:bg-green-700'}`}
        >
          📍 Locations
        </button>

        <button
          onClick={() => handleTabClick('Distribution')}
          className={`w-full px-4 py-3 rounded-lg border border-green-500 text-sm font-medium transition-colors duration-200
            ${activeTab === 'Distribution' ? 'bg-green-400 text-yellow-200 shadow-md' : 'bg-green-600  text-white hover:bg-green-700'}`}
        >
          📤 Distribution
        </button>

        <button
          onClick={() => handleTabClick('Seek MOM Expert')}
          className={`w-full px-4 py-3 rounded-lg border border-green-500 text-sm font-medium transition-colors duration-200
            ${activeTab === 'Seek MOM Expert' ? 'bg-green-400 text-yellow-200 shadow-md' : 'bg-green-600  text-white hover:bg-green-700'}`}
        >
          🧠 Seek MOM Expert
        </button>

        <button
          onClick={() => handleTabClick('Purpose')}
          className={`w-full px-4 py-3 rounded-lg border border-green-500 text-sm font-medium transition-colors duration-200
            ${activeTab === 'Purpose' ? 'bg-green-400 text-yellow-200 shadow-md' : 'bg-green-600  text-white hover:bg-green-700'}`}
        >
          🎯 Purpose
        </button>
      </div>


      {/* Back Button for tabs */}
      {activeTab && (
        <button onClick={handleBackToMeetings} style={styles.backButton}>
          ← Back to Meetings
        </button>
      )}

      {/* Content based on view */}
      {currentView === 'meetings' && (
        <ul style={styles.meetingsList}>
          {filteredMeetings.length > 0 ? (
            filteredMeetings.map((meeting) => (
              <li
                key={meeting.id}
                onClick={() => handleMeetingClick(meeting)}
                style={{ ...styles.listItem, ...styles.meetingItem }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <h3 style={styles.meetingTitle}>{meeting.title}</h3>
                <p style={styles.meetingDetails}>Date: {meeting.date} | Attendees: {meeting.attendees} | Status: {meeting.status}</p>
              </li>
            ))
          ) : (
            <li style={styles.noItems}>No meetings found matching your search.</li>
          )}
        </ul>
      )}

      {currentView === 'details' && renderMeetingDetails(selectedMeeting)}

      {activeTab && (
        <div>
          <h2 style={{ ...styles.header, fontSize: '1.25rem', marginBottom: '1rem' }}>{activeTab}</h2>
          {renderTabContent(activeTab) || <p style={styles.noItems}>No data available for this section.</p>}
        </div>
      )}
    </div>
  );
};

export default MOMHome;